import { Bath, Bed, CalendarDays, FileText, Image as ImageIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PropertyThumbnailImage } from "./PropertyThumbnailImage";
import { AvailabilityDot } from "@/components/atoms/AvailabilityDot";
import { Badge } from "@/components/atoms/Badge";
import { formatPrice, formatRelativeTime } from "@/lib/format";
import type { PropertySummary } from "@/types/entities";

interface Props {
  rows: PropertySummary[];
  imageUrlsByPropertyId?: Record<string, string[]>;
  fallbackLogoUrlByPropertyId?: Record<string, string>;
  onSelect: (row: PropertySummary) => void;
  onAvailability?: (row: PropertySummary) => void;
  returnView?: "list" | "tile";
  /**
   * Opt-in multi-select, mirroring the list view's checkbox column so a selection
   * made in one view is still visible (and extendable) in the other.
   */
  selection?: {
    selectedKeys: Set<string>;
    onToggle: (row: PropertySummary, next: boolean) => void;
  };
}

function TileImage({
  urls,
  fallbackLogoUrl,
  alt,
}: {
  urls: string[] | undefined;
  fallbackLogoUrl: string;
  alt: string;
}) {
  return (
    <PropertyThumbnailImage
      urls={urls}
      fallbackLogoUrl={fallbackLogoUrl}
      alt={alt}
      fallbackLogoSize={44}
    />
  );
}

export function PropertiesTileGrid({
  rows,
  imageUrlsByPropertyId = {},
  fallbackLogoUrlByPropertyId = {},
  onSelect,
  onAvailability,
  returnView = "tile",
  selection,
}: Props) {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-4">
      {rows.map((property) => {
        const checked = selection?.selectedKeys.has(property.id) ?? false;
        return (
        <div
          key={property.id}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(property)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") onSelect(property);
          }}
          className={`overflow-hidden rounded-xl border bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-ring ${
            checked ? "border-[#009BF5] ring-2 ring-[#009BF5]/25" : "border-bridge-border/70"
          }`}
        >
          <div className="relative aspect-[4/3] overflow-hidden bg-bridge-surface">
            <TileImage
              urls={imageUrlsByPropertyId[property.id]}
              fallbackLogoUrl={fallbackLogoUrlByPropertyId[property.id] ?? ""}
              alt={property.displayAddress ?? "Property"}
            />
            {selection ? (
              <label
                onClick={(event) => event.stopPropagation()}
                className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-md bg-white/90 shadow-sm backdrop-blur"
              >
                <input
                  type="checkbox"
                  aria-label={`Select ${property.displayAddress ?? property.id}`}
                  checked={checked}
                  onChange={(event) => selection.onToggle(property, event.target.checked)}
                  className="h-4 w-4 cursor-pointer rounded border-slate-300 text-[#009BF5] focus:ring-2 focus:ring-[#009BF5]/40 focus:ring-offset-0"
                />
              </label>
            ) : null}
            {property.availabilityStatus ? (
              <Badge
                tone="accent"
                className={`absolute top-2 rounded-full px-2.5 ${selection ? "left-10" : "left-2"}`}
              >
                {property.availabilityStatus}
              </Badge>
            ) : null}
            {property.market ? (
              <span className="absolute right-2 top-2 rounded-md bg-ink-900/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                {property.market}
              </span>
            ) : null}
            <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-white/90 p-1 shadow-sm backdrop-blur">
              {onAvailability ? (
                <button
                  type="button"
                  title="Availability"
                  aria-label="Availability"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAvailability(property);
                  }}
                  className="rounded-full p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface hover:text-[#009BF5]"
                >
                  <CalendarDays size={14} />
                </button>
              ) : null}
              <button
                type="button"
                title="View documents"
                aria-label="View documents"
                onClick={(event) => {
                  event.stopPropagation();
                  const params = new URLSearchParams();
                  if (property.externalId) params.set("externalPropertyId", property.externalId);
                  params.set("returnView", returnView);
                  navigate(`/documents?${params.toString()}`);
                }}
                className="rounded-full p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface hover:text-[#009BF5]"
              >
                <FileText size={14} />
              </button>
              <button
                type="button"
                title="View images"
                aria-label="View images"
                onClick={(event) => {
                  event.stopPropagation();
                  const params = new URLSearchParams({ propertyId: property.id });
                  if (property.externalId) params.set("externalPropertyId", property.externalId);
                  params.set("returnView", returnView);
                  navigate(`/images?${params.toString()}`);
                }}
                className="rounded-full p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface hover:text-[#009BF5]"
              >
                <ImageIcon size={14} />
              </button>
            </div>
          </div>
          <div className="space-y-2 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <AvailabilityDot color={property.availabilityColor} />
                  <span className="truncate text-sm font-semibold text-slate-800">
                    {property.displayAddress ?? "(no address)"}
                  </span>
                </div>
                <div className="mt-0.5 truncate text-xs text-bridge-muted">{property.propertyType ?? "—"}</div>
              </div>
              <div className="shrink-0 text-sm font-semibold text-slate-900">
                {formatPrice(property.price, property.currency)}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 text-xs text-bridge-muted">
              <div className="flex items-center gap-3 text-slate-700">
                <span className="inline-flex items-center gap-1">
                  <Bed size={12} /> {property.bedrooms ?? "—"}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Bath size={12} /> {property.bathrooms ?? "—"}
                </span>
              </div>
              <span>{formatRelativeTime(property.atlasModifiedAt)}</span>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}
